import React, { useState, useEffect } from "react";
import BrandedProposalEditor from "@/altzero_plugins/features/scopingai/scopingAi/features/scopingAi/pages/BrandedProposalEditor";
import type { BrandWorkflowConfig } from "@/altzero_plugins/features/scopingai/scopingAi/features/scopingAi/types/brandWorkflowConfig";
import { getActiveWorkspaceId } from "../../header/activeWorkspace";
import { supabaseRevisr } from "../../features/revisr/utils/supabaseRevisrClient";
import BrandingService from "../../foundation-overrides/services/brandingService";
import { usePolicyApproval } from "../../features/revisr/hooks/usePolicyApproval";
import { getApprovalState, requestApproval, getBoostPolicyById, approvePolicy, rejectPolicy } from "../../features/revisr/services/boostPolicyService";
import type { PolicyApprovalState } from "../../features/revisr/services/boostPolicyService";
import { contentPagesToMarkdown } from "../../features/revisr/utils/platePagesToMarkdown";

// Fields we care about for branding the editor. Mirrors base_organisation_branding columns
// so the PlateJS editor (which reads `primary_color`, `secondary_color`, etc.) consumes them directly.
type OrgBranding = {
  logo_url?: string | null;
  primary_color?: string | null;
  secondary_color?: string | null;
  font_color?: string | null;
  font_family?: string | null;
  brand_title?: string | null;
  tagline?: string | null;
};

// Fetch the active workspace's branding from base_organisation_branding.
// Returns null if no row exists or fetch fails (non-fatal — editor uses fallbacks).
async function fetchOrgBranding(): Promise<OrgBranding | null> {
  try {
    const orgId = getActiveWorkspaceId();
    if (!orgId) return null;
    const branding = await BrandingService.getOrganizationBranding(orgId);
    return (branding as OrgBranding | null) ?? null;
  } catch {
    return null;
  }
}

// Merge fetched org branding into an existing branding object, keeping any values
// that were already populated (e.g. the policy snapshot's logo path).
function mergeBranding(
  existing: Record<string, unknown> | null,
  orgBranding: OrgBranding | null,
): Record<string, unknown> | null {
  if (!orgBranding) return existing;
  const merged: Record<string, unknown> = { ...(existing ?? {}) };
  const keys: (keyof OrgBranding)[] = [
    'logo_url', 'primary_color', 'secondary_color', 'font_color',
    'font_family', 'brand_title', 'tagline',
  ];
  for (const k of keys) {
    if (merged[k] == null && orgBranding[k] != null) merged[k] = orgBranding[k];
  }
  return merged;
}

type BrandPolicyEditorWrapperProps = {
  keyProp?: string;
  config: BrandWorkflowConfig;
};

// Placeholder patterns that map to the active workspace / org name.
// Mirrors the KNOWN_VARIABLES mapping on the backend (brandPolicy.ts).
const ORG_PATTERNS: RegExp[] = [
  /\{\{organisation_name\}\}/gi,
  /\{\{organization_name\}\}/gi,
  /\{\{workspace_name\}\}/gi,
  /\{\{org_name\}\}/gi,
  /\{\{company_name\}\}/gi,
  /\[Top Management\]/gi,
  /\[Organisation Name\]/gi,
];

const DATE_PATTERNS: [RegExp, string][] = [
  [/\{\{date\}\}/gi, new Date().toLocaleDateString('en-AU', { day: '2-digit', month: 'long', year: 'numeric' })],
  [/\{\{current_date\}\}/gi, new Date().toLocaleDateString('en-AU', { day: '2-digit', month: 'long', year: 'numeric' })],
];

function resolveText(text: string, orgName: string, policyName: string): string {
  let result = text;
  for (const pattern of ORG_PATTERNS) result = result.replace(pattern, orgName);
  for (const [pattern, value] of DATE_PATTERNS) result = result.replace(pattern, value);
  result = result.replace(/\{\{policy_name\}\}/gi, policyName);
  return result;
}

function resolveBlock(block: unknown, orgName: string, policyName: string): unknown {
  if (!block || typeof block !== 'object') return block;
  const b = block as Record<string, unknown>;
  const resolved: Record<string, unknown> = { ...b };
  if (resolved.content && typeof resolved.content === 'object') {
    const c = { ...(resolved.content as Record<string, unknown>) };
    if (typeof c.text === 'string') c.text = resolveText(c.text, orgName, policyName);
    if (typeof c.title === 'string') c.title = resolveText(c.title, orgName, policyName);
    if (typeof c.html === 'string') c.html = resolveText(c.html, orgName, policyName);
    resolved.content = c;
  }
  if (Array.isArray(resolved.children)) {
    resolved.children = resolved.children.map((child) => resolveBlock(child, orgName, policyName));
  }
  return resolved;
}

function resolvePages(pages: unknown[], orgName: string, policyName: string): unknown[] {
  return pages.map((page) => {
    if (!page || typeof page !== 'object') return page;
    const p = page as Record<string, unknown>;
    return {
      ...p,
      blocks: Array.isArray(p.blocks)
        ? p.blocks.map((b) => resolveBlock(b, orgName, policyName))
        : p.blocks,
    };
  });
}

// Fetch the active workspace name from base_tenants using the active workspace ID.
async function fetchOrgName(): Promise<string | null> {
  try {
    const orgId = getActiveWorkspaceId();
    if (!orgId) return null;
    const client = supabaseRevisr as unknown as {
      schema: (s: string) => {
        from: (t: string) => {
          select: (c: string) => {
            eq: (col: string, val: string) => {
              single: () => Promise<{ data: { tenant_name: string } | null }>
            }
          }
        }
      }
    };
    const { data } = await client.schema('altzero').from('base_tenants').select('tenant_name').eq('tenant_id', orgId).single();
    return data?.tenant_name ?? null;
  } catch {
    return null;
  }
}

// Pre-resolve org/date placeholders in session storage before the editor reads it.
// This is a safety net for cases where the backend couldn't resolve them
// (old sessions, cross-tab reload, or silent org query failure during generation).
function patchSessionData(dataKey: string, orgName: string, policyName: string): void {
  try {
    const raw = sessionStorage.getItem(dataKey);
    if (!raw) return;
    const data = JSON.parse(raw) as Record<string, unknown>;
    const template = data.selectedTemplate as { pages?: unknown[] } | null | undefined;
    if (!template?.pages?.length) return;
    const resolvedPages = resolvePages(template.pages, orgName, policyName);
    data.selectedTemplate = { ...template, pages: resolvedPages };
    sessionStorage.setItem(dataKey, JSON.stringify(data));
  } catch {
    // Non-fatal — editor will still render with whatever is in session
  }
}

function patchSessionResult(resultKey: string, orgName: string, policyName: string): void {
  try {
    const raw = sessionStorage.getItem(resultKey);
    if (!raw) return;
    const result = JSON.parse(raw) as Record<string, unknown>;
    const template = result.selectedTemplate as { pages?: unknown[] } | null | undefined;
    if (!template?.pages?.length) return;
    const resolvedPages = resolvePages(template.pages, orgName, policyName);
    result.selectedTemplate = { ...template, pages: resolvedPages };
    sessionStorage.setItem(resultKey, JSON.stringify(result));
  } catch {
    // Non-fatal
  }
}

// Merge fetched org branding into both session payloads so BrandedProposalEditor's
// `branding` state populates with colors/font even when the upstream caller only
// stored `{ logo_url }`. Existing fields win — we never overwrite a snapshot.
function patchSessionBranding(
  dataKey: string,
  resultKey: string,
  orgBranding: OrgBranding,
): void {
  try {
    const dataRaw = sessionStorage.getItem(dataKey);
    if (dataRaw) {
      const data = JSON.parse(dataRaw) as Record<string, unknown>;
      const merged = mergeBranding(
        (data.brandingData as Record<string, unknown> | null) ?? null,
        orgBranding,
      );
      data.brandingData = merged;
      sessionStorage.setItem(dataKey, JSON.stringify(data));
    }
  } catch {
    // Non-fatal
  }
  try {
    const resultRaw = sessionStorage.getItem(resultKey);
    if (resultRaw) {
      const result = JSON.parse(resultRaw) as Record<string, unknown>;
      const merged = mergeBranding(
        (result.branding as Record<string, unknown> | null) ?? null,
        orgBranding,
      );
      result.branding = merged;
      sessionStorage.setItem(resultKey, JSON.stringify(result));
    }
  } catch {
    // Non-fatal
  }
}

export const BrandPolicyEditorWrapper: React.FC<BrandPolicyEditorWrapperProps> = ({
  keyProp,
  config,
}) => {
  const [orgName, setOrgName] = useState<string | null>(null);
  const [orgNameReady, setOrgNameReady] = useState(false);
  // Branding pulled from base_organisation_branding so the editor's CSS variables
  // (primary/secondary/font color, fontFamily) populate even when the policy record's
  // branding_data snapshot is missing fields (e.g. older records, or paths that didn't
  // snapshot branding at generation time).
  const [orgBranding, setOrgBranding] = useState<OrgBranding | null>(null);

  // ── Policy approval workflow ──────────────────────────────────────────────
  // When the flag is on and the viewer is a member, fetch the policy's approval
  // state so the editor can gate Save/Export. Org admins bypass (no fetch needed).
  const { approvalRequired, isOrgAdmin } = usePolicyApproval();
  const [approvalState, setApprovalState] = useState<PolicyApprovalState>("none");
  const [rejectedReason, setRejectedReason] = useState<string | null>(null);

  useEffect(() => {
    if (!approvalRequired || isOrgAdmin) {
      setApprovalState("none");
      setRejectedReason(null);
      return;
    }
    const policyId =
      new URLSearchParams(window.location.search).get("id") ||
      sessionStorage.getItem("boost_policy_record_id");
    if (!policyId) return;
    let cancelled = false;
    getBoostPolicyById(policyId)
      .then((record) => {
        if (cancelled) return;
        const st = getApprovalState(record);
        setApprovalState(st);
        const meta = (record as { metadata?: { approval?: { reason?: string } } | null } | null)?.metadata;
        setRejectedReason(st === "rejected" ? meta?.approval?.reason ?? null : null);
      })
      .catch(() => { /* non-fatal — default to not approved (gated) */ });
    return () => { cancelled = true; };
  }, [approvalRequired, isOrgAdmin]);

  // Fetch org name + branding once on mount — used to patch session AND wrap loadFn
  useEffect(() => {
    Promise.all([fetchOrgName(), fetchOrgBranding()])
      .then(([name, branding]) => {
        if (name) setOrgName(name);
        if (branding) setOrgBranding(branding);
      })
      .catch(() => { /* non-fatal */ })
      .finally(() => setOrgNameReady(true));
  }, []);

  // Patch session storage (same-tab flow) as soon as org name + branding are available.
  // Merges fetched org branding into the session payloads so BrandedProposalEditor's
  // `branding` state populates with colors even when the caller (BoostPolicyGenerating,
  // direct route reload, etc.) only stored a logo URL.
  useEffect(() => {
    if (!orgNameReady) return;
    const dataKey = config.session?.dataKey ?? "revisr_brand_policy_data";
    const resultKey = config.session?.resultKey ?? "revisr_brand_policy_result";

    let policyName = "Policy";
    try {
      const raw = sessionStorage.getItem(resultKey);
      if (raw) {
        const parsed = JSON.parse(raw) as { title?: string };
        if (parsed.title) policyName = parsed.title;
      }
    } catch { /* non-fatal */ }

    if (orgName) {
      patchSessionData(dataKey, orgName, policyName);
      patchSessionResult(resultKey, orgName, policyName);
    }

    if (orgBranding) {
      patchSessionBranding(dataKey, resultKey, orgBranding);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgNameReady, orgName, orgBranding]);

  // Wrap config.loadFn so DB-loaded template pages are also resolved
  // (covers new-tab / browser-reload / direct URL access without session)
  // AND so the branding object is enriched with org-level fields (colors, font)
  // that aren't snapshotted in the policy record's branding_data column.
  // Admin review mode: an org admin opened this preview from the Policy Approvals
  // queue (?review=approval). The editor then shows Accept/Reject (+ reason)
  // instead of the member Save/Export actions.
  const reviewMode = new URLSearchParams(window.location.search).get("review") === "approval";

  const resolvedConfig = React.useMemo<BrandWorkflowConfig>(() => {
    // Inject the approval gate when the flag is on and the viewer is a member.
    // Org admins and flag-off configs get no `approval` field → editor unchanged.
    const approvalEnabled = approvalRequired && !isOrgAdmin;
    const base: BrandWorkflowConfig = {
      ...config,
      orgName: orgName ?? config.orgName,
      ...(approvalEnabled
        ? {
            approval: {
              required: true,
              isOrgAdmin: false,
              state: approvalState,
              rejectedReason,
              approved: approvalState === "approved",
              requestApproval: async (params: {
                title: string;
                pages: unknown[];
                zoom: number;
                userId: string;
                orgId?: string;
                footer?: { classification?: string };
                includeCoverToc?: boolean;
              }) => {
                const policyId =
                  new URLSearchParams(window.location.search).get("id") ||
                  sessionStorage.getItem("boost_policy_record_id");
                if (!policyId) throw new Error("No policy record to request approval for");
                await requestApproval(policyId, {
                  title: params.title,
                  pages: params.pages,
                  zoom: params.zoom,
                  markdown: contentPagesToMarkdown(params.pages as { plateValue?: unknown }[]),
                  footer: params.footer,
                  includeCoverToc: params.includeCoverToc,
                });
              },
            },
          }
        : {}),
      ...(reviewMode && isOrgAdmin
        ? {
            approvalReview: {
              onApprove: async () => {
                const policyId =
                  new URLSearchParams(window.location.search).get("id") ||
                  sessionStorage.getItem("boost_policy_record_id");
                if (!policyId) throw new Error("No policy record to approve");
                await approvePolicy(policyId);
              },
              onReject: async (reason?: string) => {
                const policyId =
                  new URLSearchParams(window.location.search).get("id") ||
                  sessionStorage.getItem("boost_policy_record_id");
                if (!policyId) throw new Error("No policy record to reject");
                await rejectPolicy(policyId, reason);
              },
            },
          }
        : {}),
    };
    if (!config.loadFn) return base;
    return {
      ...base,
      loadFn: async (policyId: string) => {
        const result = await config.loadFn!(policyId);
        if (!result) return result;

        const currentOrgName = orgName ?? (await fetchOrgName());
        const currentBranding = orgBranding ?? (await fetchOrgBranding());

        // Merge org branding into the record's branding snapshot so the editor
        // picks up primary/secondary/font color + font_family. Existing snapshot
        // fields take precedence — we never overwrite an explicit policy-level value.
        const mergedBranding = mergeBranding(result.branding, currentBranding);

        if (!currentOrgName) {
          return { ...result, branding: mergedBranding };
        }

        const policyName = result.title || "Policy";

        // Resolve placeholders in template pages (cover page, etc.)
        const template = result.selectedTemplate;
        const resolvedTemplate = template?.pages?.length
          ? { ...template, pages: resolvePages(template.pages, currentOrgName, policyName) }
          : template;

        // Resolve placeholders in directly-stored editor pages (final-save format)
        const resolvedPages = result.pages?.length
          ? resolvePages(result.pages, currentOrgName, policyName)
          : result.pages;

        return {
          ...result,
          branding: mergedBranding,
          selectedTemplate: resolvedTemplate ?? null,
          pages: resolvedPages,
        };
      },
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config, orgName, orgBranding, approvalRequired, isOrgAdmin, approvalState, rejectedReason, reviewMode]);

  if (!orgNameReady) return null;

  return (
    <div className="revisr-policy-editor h-full">
      <style>{`
        .revisr-policy-editor button[title="Import Template (HTML/JSON)"] {
          display: none !important;
        }
      `}</style>
      <BrandedProposalEditor key={keyProp} config={resolvedConfig} />
    </div>
  );
};
